import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { SiteLogo } from "@/components/SiteLogo";
import { SITE_NAME, SITE_TAGLINE } from "@shared/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Link } from "wouter";
import { CheckCircle, ArrowLeft, Mail } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const prev = document.title;
    document.title = `Contact — ${SITE_NAME}`;
    return () => {
      document.title = prev;
    };
  }, []);

  const send = trpc.forms.contact.useMutation({
    onSuccess: () => setSubmitted(true),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    send.mutate(form);
  };

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="border-b border-border/50 bg-background/95 backdrop-blur-md sticky top-0 z-40">
        <div className="container flex items-center gap-3 h-14">
          <Link href="/">
            <button className="flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors text-sm font-medium">
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
          </Link>
          <span className="text-border/50">|</span>
          <Link href="/" className="text-foreground hover:opacity-90 transition-opacity flex items-center gap-2">
            <SiteLogo className="h-8" />
            <span className="sr-only">
              {SITE_NAME} — {SITE_TAGLINE}
            </span>
          </Link>
        </div>
      </header>

      <main className="container py-12 max-w-xl mx-auto">
        {submitted ? (
          <div className="flex flex-col items-center gap-5 py-16 text-center">
            <CheckCircle className="w-14 h-14" style={{ color: "var(--pb-green)" }} />
            <h2
              className="text-3xl font-black tracking-tight"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              MESSAGE SENT!
            </h2>
            <p className="text-muted-foreground">
              Thanks for reaching out. We'll get back to you at <strong>{form.email}</strong> as soon as possible.
            </p>
            <Link href="/">
              <Button
                className="mt-2 font-bold text-black"
                style={{ background: "var(--pb-green)" }}
              >
                Back to Radio
              </Button>
            </Link>
          </div>
        ) : (
          <>
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <Mail className="w-5 h-5" style={{ color: "var(--pb-green)" }} />
                <span
                  className="text-xs font-bold tracking-widest uppercase"
                  style={{ color: "var(--pb-green)" }}
                >
                  Get in Touch
                </span>
              </div>
              <h1
                className="text-4xl font-black tracking-tight mb-2"
                style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
              >
                CONTACT US
              </h1>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Have a question, feedback, or just want to say hi? Drop us a line and we'll get back to you.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="name" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Your Name *
                  </Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Jane Smith"
                    required
                    className="bg-card border-border/60 focus:border-[var(--pb-green)]"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                    Email Address *
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    placeholder="jane@example.com"
                    required
                    className="bg-card border-border/60 focus:border-[var(--pb-green)]"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="subject" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Subject *
                </Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={set("subject")}
                  placeholder="What's on your mind?"
                  required
                  className="bg-card border-border/60 focus:border-[var(--pb-green)]"
                />
              </div>

              <div className="flex flex-col gap-1.5">
                <Label htmlFor="message" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Message *
                </Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Tell us what's on your mind..."
                  required
                  rows={6}
                  className="bg-card border-border/60 focus:border-[var(--pb-green)] resize-none"
                />
              </div>

              {send.error && (
                <p className="text-destructive text-sm">
                  {send.error.message || "Something went wrong. Please try again."}
                </p>
              )}

              <Button
                type="submit"
                disabled={send.isPending}
                className="w-full font-bold text-black text-base py-3 h-auto"
                style={{ background: "var(--pb-green)" }}
              >
                {send.isPending ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </>
        )}
      </main>
    </div>
  );
}
